import type { BrandOption } from '@shared/schema';
import { Link } from 'wouter';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Loader2 } from 'lucide-react';


interface CreateProjectModalProps {
  onClose: () => void;
  onCreated: (projectId: number) => void;
}

export function CreateProjectModal({ onClose, onCreated }: CreateProjectModalProps) {
  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [brandId, setBrandId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { data: brands = [], isLoading: brandsLoading, isError: brandsError } = useQuery<BrandOption[]>({
    queryKey: ['/api/brands', 'options'],
    staleTime: 60000,
  });

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || !brandId || isCreating) return;
    setIsCreating(true);
    try {
      const resp = await fetch('/api/canvas/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: trimmedName,
          brandId: brandId || null,
          instructions: instructions.trim() || null,
          isFolder: true,
        }),
      });
      if (resp.ok) {
        const project = await resp.json();
        onCreated(project.id);
      }
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(0,0,0,0.25)', backdropFilter: 'blur(2px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative bg-white rounded-2xl overflow-hidden"
        style={{ width: 480, maxWidth: 'calc(100vw - 32px)', boxShadow: '0 8px 40px rgba(0,0,0,0.14)', border: '1px solid #DCE3ED' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between"
          style={{ padding: '20px 24px 16px', borderBottom: '1px solid #EAF0F7' }}
        >
          <h2 style={{ fontSize: 16, fontWeight: 600, color: '#1A1A1A' }}>New project</h2>
          <button
            aria-label="Close new project"
            onClick={onClose}
            className="flex items-center justify-center rounded-lg transition-colors"
            style={{ width: 28, height: 28, color: '#5F6D82' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#EAF0F7'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          {/* Name */}
          <div className="mb-4">
            <label htmlFor="new-project-name" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#5F6D82', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Project name
            </label>
            <input
              id="new-project-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
              placeholder="e.g. Summer Campaign 2026"
              className="w-full rounded-xl px-4 py-2.5 outline-none transition-colors"
              style={{ fontSize: 14, color: '#1A1A1A', border: '1px solid #DCDCDC', background: '#F7F9FC' }}
              onFocus={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = '#94A3B8'; }}
              onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = '#DCDCDC'; }}
            />
          </div>

          {/* Instructions */}
          <div className="mb-4">
            <label htmlFor="new-project-instructions" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#5F6D82', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Instructions <span style={{ textTransform: 'none', fontWeight: 400, color: '#5F6D82' }}>(optional)</span>
            </label>
            <textarea
              id="new-project-instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Describe the goal or context for this project..."
              rows={3}
              className="w-full rounded-xl px-4 py-2.5 resize-none outline-none transition-colors"
              style={{ fontSize: 14, color: '#1A1A1A', border: '1px solid #DCDCDC', background: '#F7F9FC', lineHeight: 1.6 }}
              onFocus={(e) => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = '#94A3B8'; }}
              onBlur={(e) => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = '#DCDCDC'; }}
            />
          </div>

          {/* Brand */}
          <div className="mb-2">
            <label htmlFor="new-project-brand" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#5F6D82', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Brand
            </label>
            <select
              id="new-project-brand"
              value={brandId ?? ''}
              onChange={(e) => setBrandId(e.target.value ? Number(e.target.value) : null)}
              className="w-full rounded-xl px-4 py-2.5 outline-none transition-colors"
              style={{ fontSize: 14, color: brandId ? '#1A1A1A' : '#5F6D82', border: '1px solid #DCE3ED', background: '#F7F9FC' }}
            >
              <option value="">Select a brand…</option>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            {!brandId && (
              <p style={{ fontSize: 12, color: '#5F6D82', marginTop: 8 }}>Choose the brand this project belongs to.</p>
            )}
            {!brandsLoading && !brandsError && brands.length === 0 && (
              <p className="workspace-page-description">Add a brand before creating your first project. <Link href="/brands" onClick={onClose} className="workspace-inline-link">Go to brands &amp; products</Link></p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3"
          style={{ padding: '12px 24px 20px' }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-medium transition-colors"
            style={{ color: '#5F6D82', background: 'transparent' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#F1F5F9'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!name.trim() || !brandId || isCreating}
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-medium transition-all"
            style={{
              background: name.trim() && brandId ? '#1A1A1A' : '#DCE3ED',
              color: name.trim() && brandId ? '#FFFFFF' : '#94A3B8',
              cursor: name.trim() && brandId ? 'pointer' : 'not-allowed',
            }}
          >
            {isCreating && <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />}
            {isCreating ? 'Creating…' : 'Create project'}
          </button>
        </div>
      </div>
    </div>
  );
}
